<script lang="ts">
  // The persistent overworld OVERLAY (HUD): the top-left duelist unit frame (portrait + name + rank +
  // DP/shards), the centre area pill, and the top-right players-online / Map / Shop / Menu buttons.
  // Extracted out of World.svelte so it's one shared component the world AND the Texture Pack editor
  // render. It reskins via the Global palette (surface / cyan / ink / gold); use Custom CSS for finer
  // control. All callbacks are passed in so it carries no world logic.
  import type { ComponentProps } from "svelte";
  import PortraitPreview from "./PortraitPreview.svelte";

  let {
    name, look = null, rankLabel = "", accent = "var(--color-cyan)", dp = 0, shards = 0,
    area = "", online = null, players = [], selfId = "", onMap, onShop, onMenu, onPlayer,
  }: {
    name: string;
    look?: ComponentProps<typeof PortraitPreview>["look"] | null;
    rankLabel?: string;
    accent?: string;
    dp?: number;
    shards?: number;
    area?: string;
    online?: number | null;
    players?: { userId: string; name: string; area?: string }[];
    selfId?: string;
    onMap: () => void;
    onShop: () => void;
    onMenu: () => void;
    onPlayer?: (userId: string) => void;
  } = $props();

  let playerListOpen = $state(false);
</script>

{#snippet hudIc(n: string, cls: string)}
  <svg viewBox="0 0 24 24" class={cls} fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {#if n === "map"}
      <path d="M14.1 6.1 8.9 3.5 3.5 6.2v14.3l5.4-2.7 5.2 2.6 5.4-2.7V3.4Z" /><path d="M8.9 3.5v14.3" /><path d="M14.1 6.1v14.3" />
    {:else if n === "shop"}
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
    {:else if n === "menu"}
      <line x1="4" x2="20" y1="6" y2="6" /><line x1="4" x2="20" y1="12" y2="12" /><line x1="4" x2="20" y1="18" y2="18" />
    {/if}
  </svg>
{/snippet}

<!-- HUD command button: charged edge + light sweep + lift on hover. -->
{#snippet hudBtn(label: string, icon: string, action: () => void)}
  <button
    onclick={action}
    class="tp-hud-btn hud-command group chamfer-btn fx-lift bg-cyan-dim/35 p-px hover:bg-cyan/70 hover:glow-cyan"
    style="--c:8px"
    aria-label={label}
    title={label}
  >
    <span
      class="hud-command-inner chamfer-btn fx-sweep flex items-center gap-1.5 bg-surface-2/95 px-3.5 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-200 group-hover:text-cyan"
      style="--c:7px"
    >
      {@render hudIc(icon, "hud-command-icon size-3.5 opacity-70 transition-opacity duration-200 group-hover:opacity-100")}
      <span class="hud-command-label">{label}</span>
    </span>
  </button>
{/snippet}

<div class="pointer-events-none fixed inset-0 z-30">
  <!-- duelist unit frame, top-left -->
  <div class="tp-hud-frame hud-unit-frame pointer-events-auto absolute top-3 flex items-stretch">
    <div class="hud-portrait relative z-10 size-[64px] shrink-0">
      <div class="chamfer absolute inset-0" style="--c:9px; background:{accent}; box-shadow:0 0 12px color-mix(in oklab, {accent} 55%, transparent)"></div>
      <div class="chamfer grid-tex absolute inset-[1.5px] overflow-hidden bg-void-2" style="--c:8px">
        <div class="hud-portrait-preview"><PortraitPreview size={61} look={look ?? undefined} windowed /></div>
        <span class="pointer-events-none absolute inset-x-2 top-0 h-px" style="background: linear-gradient(90deg, transparent, color-mix(in oklab, {accent} 85%, transparent), transparent)"></span>
      </div>
    </div>

    <div class="hud-vitals relative z-0 -ml-3 w-[214px]">
      <div class="chamfer absolute inset-0 bg-cyan-dim/45" style="--c:10px"></div>
      <div class="chamfer grid-tex absolute inset-px bg-surface/92" style="--c:9px"></div>
      <div class="pointer-events-none absolute inset-x-8 top-px h-px" style="background: linear-gradient(90deg, transparent, color-mix(in oklab, var(--color-cyan) 50%, transparent), transparent)"></div>

      <div class="hud-vitals-content relative flex h-full flex-col justify-center gap-2 py-2.5 pl-5 pr-3.5">
        <div class="flex items-center gap-2">
          <span class="min-w-0 flex-1 truncate font-display text-[15px] font-bold leading-none tracking-wide text-ink">{name}</span>
          {#if rankLabel}
            <span class="chamfer-btn shrink-0 px-1.5 py-1 font-display text-[9px] font-bold uppercase leading-none tracking-[0.08em]" style="--c:4px; color:{accent}; background:color-mix(in oklab, {accent} 15%, transparent); box-shadow:inset 0 0 0 1px color-mix(in oklab, {accent} 42%, transparent)">{rankLabel}</span>
          {/if}
        </div>
        <div class="flex items-center gap-2.5">
          <span class="flex items-center gap-1.5" title="Duel Points">
            <span class="font-display text-[10px] font-semibold uppercase tracking-[0.06em] text-gold/80">DP</span>
            <span class="font-display text-[14px] font-bold leading-none text-gold tabular-nums">{dp.toLocaleString()}</span>
          </span>
          <span class="h-3.5 w-px bg-cyan-dim/30"></span>
          <span class="flex items-center gap-1.5" title="Shards, craft cards in the Deck Editor">
            <span class="size-2 rotate-45 rounded-[1px] bg-cyan" style="box-shadow: 0 0 5px var(--color-cyan)"></span>
            <span class="font-display text-[14px] font-bold leading-none text-cyan tabular-nums">{shards.toLocaleString()}</span>
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- area pill, top-centre (a data line scans beneath the location name).
       Phones: capped + truncated so it clears the right-side button cluster. -->
  <div class="hud-area absolute left-1/2 top-3 -translate-x-1/2 phone:max-w-[28vw]">
    <div class="tp-hud-area chamfer-btn relative overflow-hidden bg-surface/80 px-5 py-1.5 glow-cyan" style="--c:9px">
      <span class="block truncate font-display text-sm font-bold uppercase tracking-[0.18em] text-cyan text-glow">{area}</span>
      <span class="pointer-events-none absolute inset-x-3 bottom-[3px] h-px overflow-hidden" aria-hidden="true">
        <span class="area-scan block h-full w-1/3 bg-gradient-to-r from-transparent via-cyan/80 to-transparent"></span>
      </span>
    </div>
  </div>

  <!-- players + Map / Shop / Menu, top-right -->
  <div class="hud-actions pointer-events-auto absolute top-3 flex items-center gap-2">
    <div class="relative">
      <button
        onclick={() => { if (online !== null) playerListOpen = !playerListOpen; }}
        disabled={online === null}
        class="tp-hud-btn hud-online chamfer-btn fx-lift fx-sweep bg-surface/70 px-3 py-2 font-display text-xs tracking-widest hover:bg-surface hover:text-cyan {playerListOpen ? 'text-cyan glow-cyan' : 'text-ink-dim'}"
        style="--c:7px"
        title="Players online"
        aria-label={online === null ? "Players online: updating" : `Players online: ${online}`}
        aria-busy={online === null}
        aria-expanded={playerListOpen}
        aria-haspopup="listbox"
      >
        <span aria-hidden="true">◇</span> <span>{online ?? "…"}</span><span class="hud-online-label">&nbsp;online</span>
      </button>
      {#if playerListOpen}
        <div class="chamfer absolute right-0 top-[calc(100%+6px)] max-h-[55dvh] w-52 overflow-y-auto bg-void/90 p-1.5 ring-1 ring-cyan/25" style="--c:9px">
          {#each players.filter((p) => p.userId !== selfId) as p (p.userId)}
            <button onclick={() => { onPlayer?.(p.userId); playerListOpen = false; }} class="group flex w-full items-center gap-1.5 px-2 py-1 text-left font-body text-sm text-ink transition-colors hover:bg-surface/60 hover:text-cyan"><span class="size-1 shrink-0 rotate-45 bg-cyan/0 transition-colors group-hover:bg-cyan" aria-hidden="true"></span><span class="min-w-0 flex-1"><span class="block truncate leading-tight">{p.name}</span>{#if p.area}<span class="block truncate font-display text-[0.58rem] uppercase leading-tight tracking-[0.12em] text-ink-dim/70">{p.area}</span>{/if}</span></button>
          {:else}
            <div class="px-2 py-1.5 text-center font-display text-[0.66rem] uppercase tracking-[0.14em] text-ink-dim/55">No one else online</div>
          {/each}
        </div>
      {/if}
    </div>
    {@render hudBtn("Map", "map", onMap)}
    {@render hudBtn("Shop", "shop", onShop)}
    {@render hudBtn("Menu", "menu", onMenu)}
  </div>
</div>

<style>
  /* Keep primary controls near the centred playfield on monitors wider than the
     game's useful command field. --world-ui-gutter resolves to 12px elsewhere. */
  .hud-unit-frame {
    left: var(--world-ui-gutter);
    transform: scale(var(--world-ui-scale));
    transform-origin: top left;
  }
  .hud-actions {
    right: var(--world-ui-gutter);
    transform: scale(var(--world-ui-scale));
    transform-origin: top right;
  }
  .hud-area {
    transform: translateX(-50%) scale(var(--world-ui-scale));
    transform-origin: top center;
  }

  /* Location readout: a thin data line sweeps beneath the name (a live HUD tell). */
  .area-scan {
    animation: area-scan 3.2s ease-in-out infinite;
  }
  @keyframes area-scan {
    0% {
      transform: translateX(-110%);
    }
    55%,
    100% {
      transform: translateX(410%);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .area-scan {
      animation: none;
      opacity: 0;
    }
  }

  /* Landscape phones: the world stays primary. Keep identity and currency,
     remove redundant location chrome, and use 44px icon command targets. */
  :global(.phone) .hud-area {
    display: none;
  }
  :global(.phone) .hud-unit-frame {
    left: max(0.5rem, env(safe-area-inset-left));
    top: max(0.5rem, env(safe-area-inset-top));
    height: 52px;
  }
  :global(.phone) .hud-portrait {
    width: 52px;
    height: 52px;
  }
  :global(.phone) .hud-portrait-preview {
    transform: scale(0.805);
    transform-origin: top left;
  }
  :global(.phone) .hud-vitals {
    width: 164px;
  }
  :global(.phone) .hud-vitals-content {
    gap: 0.4rem;
    padding: 0.4rem 0.65rem 0.4rem 1.15rem;
  }
  :global(.phone) .hud-actions {
    right: max(0.5rem, env(safe-area-inset-right));
    top: max(0.5rem, env(safe-area-inset-top));
    gap: 0.35rem;
  }
  :global(.phone) .hud-command,
  :global(.phone) .hud-online {
    width: 44px;
    height: 44px;
    padding: 1px;
  }
  :global(.phone) .hud-command-inner {
    width: 100%;
    height: 100%;
    justify-content: center;
    padding: 0;
  }
  :global(.phone) .hud-command-icon {
    width: 19px;
    height: 19px;
    opacity: 0.9;
  }
  :global(.phone) .hud-command-label,
  :global(.phone) .hud-online-label {
    display: none;
  }
  :global(.phone) .hud-online {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    font-size: 0.7rem;
    letter-spacing: 0;
  }
</style>
